import { useState } from 'react';
import {
  useSystemNotifications,
  useCreateSystemNotification,
  useUpdateSystemNotification,
  useDeleteSystemNotification,
  usePublishSystemNotification,
  useUnpublishSystemNotification,
  useAdminTeams,
  useAdminUsers,
  useAdminAgencies,
  useAdminPackages,
} from '@/hooks/useAdmin';
import {
  SystemNotificationListItem,
  CreateNotificationRequest,
  SystemNotificationFilters,
} from '@/types/admin';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog, Card, EmptyState, Pagination, Badge } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { DatePickerSingle } from '@/components/ui/DatePickerSingle';
import { PostEditor } from '@/components/composer/PostEditor';
import { Tooltip } from '@/components/ui/Tooltip';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { formatApiError } from '@/services/api';

/**
 * Admin Notifications Page
 * Manage system-wide broadcast notifications
 */

const TYPE_OPTIONS = [
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'ANNOUNCEMENT', label: 'Announcement' },
  { value: 'FEATURE', label: 'Feature' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'PROMOTION', label: 'Promotion' },
];

const SEVERITY_OPTIONS = [
  { value: 'INFO', label: 'Info' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'ERROR', label: 'Error' },
  { value: 'SUCCESS', label: 'Success' },
];

const TARGET_TYPE_OPTIONS = [
  { value: 'ALL', label: 'All Users' },
  { value: 'TEAM', label: 'Specific Teams' },
  { value: 'AGENCY', label: 'Specific Brand Portfolios' },
  { value: 'USER', label: 'Specific Users' },
  { value: 'PACKAGE', label: 'Specific Packages' },
];

// Color mappings
const TYPE_COLORS: Record<string, string> = {
  MAINTENANCE: 'bg-orange-100 text-orange-700 border-orange-200',
  ANNOUNCEMENT: 'bg-primary-50 text-primary-800 border-primary-200',
  FEATURE: 'bg-peach text-secondary-700 border-warm-gray',
  WARNING: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  PROMOTION: 'bg-green-100 text-green-700 border-green-200',
};

const SEVERITY_COLORS: Record<string, string> = {
  INFO: 'bg-blue-100 text-blue-700 border-blue-200',
  WARNING: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  ERROR: 'bg-red-100 text-red-700 border-red-200',
  SUCCESS: 'bg-green-100 text-green-700 border-green-200',
};

interface NotificationFormData {
  type: string;
  title: string;
  message: string;
  severity: string;
  targetType: string;
  targetIds: number[];
  startAt: string;
  endAt: string;
  isDismissible: boolean;
  ctaText: string;
  ctaUrl: string;
}

const initialFormData: NotificationFormData = {
  type: 'ANNOUNCEMENT',
  title: '',
  message: '',
  severity: 'INFO',
  targetType: 'ALL',
  targetIds: [],
  startAt: '',
  endAt: '',
  isDismissible: true,
  ctaText: '',
  ctaUrl: '',
};

export const AdminNotifications = () => {
  const { formatDateTime } = useTimezone();
  const [filters, setFilters] = useState<SystemNotificationFilters>({
    page: 1,
    limit: 20,
  });

  const [showModal, setShowModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState<SystemNotificationListItem | null>(null);
  const [formData, setFormData] = useState<NotificationFormData>(initialFormData);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; notification: SystemNotificationListItem | null }>({ open: false, notification: null });

  const { data, isLoading, error } = useSystemNotifications(filters);
  const createNotification = useCreateSystemNotification();
  const updateNotification = useUpdateSystemNotification();
  const deleteNotification = useDeleteSystemNotification();
  const publishNotification = usePublishSystemNotification();
  const unpublishNotification = useUnpublishSystemNotification();

  const handleCreateClick = () => {
    setEditingNotification(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const handleEditClick = (notification: SystemNotificationListItem) => {
    setEditingNotification(notification);
    setFormData({
      type: notification.type,
      title: notification.title,
      message: notification.message || '',
      severity: notification.severity,
      targetType: notification.targetType,
      targetIds: notification.targetIds || [],
      startAt: notification.startAt || '',
      endAt: notification.endAt || '',
      isDismissible: notification.isDismissible,
      ctaText: notification.ctaText || '',
      ctaUrl: notification.ctaUrl || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (isActive: boolean) => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Message is required');
      return;
    }
    if (formData.title.length > 255) {
      toast.error('Title must be 255 characters or less');
      return;
    }

    const requestData: CreateNotificationRequest & { isActive?: boolean } = {
      type: formData.type as CreateNotificationRequest['type'],
      title: formData.title,
      message: formData.message,
      severity: formData.severity as CreateNotificationRequest['severity'],
      targetType: formData.targetType as CreateNotificationRequest['targetType'],
      targetIds: formData.targetIds.length > 0 ? formData.targetIds : undefined,
      startAt: formData.startAt || undefined,
      endAt: formData.endAt || undefined,
      isDismissible: formData.isDismissible,
      ctaText: formData.ctaText || undefined,
      ctaUrl: formData.ctaUrl || undefined,
      isActive,
    };

    try {
      if (editingNotification) {
        await updateNotification.mutateAsync({
          id: editingNotification.id,
          data: requestData,
        });
        toast.success(`Notification ${isActive ? 'published' : 'saved as draft'} successfully`);
      } else {
        await createNotification.mutateAsync(requestData);
        toast.success(`Notification ${isActive ? 'published' : 'saved as draft'} successfully`);
      }
      setShowModal(false);
    } catch (err) {
      toast.error(`Failed to ${editingNotification ? 'update' : 'create'} notification`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.notification) return;

    try {
      await deleteNotification.mutateAsync(deleteDialog.notification.id);
      toast.success('Notification deleted');
      setDeleteDialog({ open: false, notification: null });
    } catch (err) {
      toast.error('Failed to delete notification');
    }
  };

  const handleToggleActive = async (notification: SystemNotificationListItem) => {
    try {
      if (notification.isActive) {
        await unpublishNotification.mutateAsync(notification.id);
        toast.success('Notification unpublished');
      } else {
        await publishNotification.mutateAsync(notification.id);
        toast.success('Notification published');
      }
    } catch (err) {
      toast.error('Failed to toggle notification status');
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        Error loading notifications: {formatApiError(error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="System Notifications"
        subtitle="Manage system-wide broadcast notifications for users"
        action={
          <Button onClick={handleCreateClick} size="md">
            <Icon name={ICONS.PLUS} size="sm" className="mr-2" />
            Create Notification
          </Button>
        }
      />

      {/* Filters */}
      <Card padding="sm" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select
            label="Type"
            placeholder="All types"
            value={filters.type || '_all'}
            onValueChange={(value) => setFilters({ ...filters, type: value === '_all' ? undefined : value, page: 1 })}
            options={[
              { value: '_all', label: 'All Types' },
              ...TYPE_OPTIONS,
            ]}
            size="sm"
          />
          <Select
            label="Severity"
            placeholder="All severities"
            value={filters.severity || '_all'}
            onValueChange={(value) => setFilters({ ...filters, severity: value === '_all' ? undefined : value, page: 1 })}
            options={[
              { value: '_all', label: 'All Severities' },
              ...SEVERITY_OPTIONS,
            ]}
            size="sm"
          />
          <Select
            label="Target"
            placeholder="All targets"
            value={filters.targetType || '_all'}
            onValueChange={(value) => setFilters({ ...filters, targetType: value === '_all' ? undefined : value, page: 1 })}
            options={[
              { value: '_all', label: 'All Targets' },
              ...TARGET_TYPE_OPTIONS,
            ]}
            size="sm"
          />
          <Select
            label="Status"
            placeholder="All statuses"
            value={filters.isActive !== undefined ? String(filters.isActive) : '_all'}
            onValueChange={(value) =>
              setFilters({
                ...filters,
                isActive: value === '_all' ? undefined : value === 'true',
                page: 1,
              })
            }
            options={[
              { value: '_all', label: 'All Statuses' },
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ]}
            size="sm"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <DatePickerSingle
            label="Created From"
            value={filters.createdFrom || ''}
            onChange={(iso) => setFilters({ ...filters, createdFrom: iso || undefined, page: 1 })}
            placeholder="dd.mm.yyyy"
          />
          <DatePickerSingle
            label="Created To"
            value={filters.createdTo || ''}
            onChange={(iso) => setFilters({ ...filters, createdTo: iso || undefined, page: 1 })}
            placeholder="dd.mm.yyyy"
            minDate={filters.createdFrom ? new Date(filters.createdFrom) : undefined}
          />
          <div className="md:col-span-2 flex justify-end">
            {(filters.type || filters.severity || filters.targetType || filters.isActive !== undefined || filters.createdFrom || filters.createdTo) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFilters({ page: 1, limit: 20 })}
              >
                <Icon name={ICONS.CLOSE} size="sm" className="mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : !data?.data || data.data.length === 0 ? (
          <EmptyState
            icon={ICONS.BELL}
            title="No notifications found"
            action={
              <Button onClick={handleCreateClick} size="sm">
                Create First Notification
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="w-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target
                  </th>
                  <th className="w-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="w-16 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <Tooltip content="Dismissals">
                      <span><i className={`hm hm-${ICONS.CLOSE}`} aria-hidden="true" /></span>
                    </Tooltip>
                  </th>
                  <th className="w-20 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.data?.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded text-xs font-medium border',
                          TYPE_COLORS[notification.type]
                        )}
                      >
                        {notification.type}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {notification.title}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {notification.creatorEmail}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded text-xs font-medium border',
                          SEVERITY_COLORS[notification.severity]
                        )}
                      >
                        {notification.severity}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700">
                      {notification.targetType}
                      {notification.targetIds?.length > 0 && (
                        <span className="ml-1 text-gray-500">
                          ({notification.targetIds.length})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(notification)}
                        className={cn(
                          'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                          notification.isActive
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                      >
                        {notification.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                      {notification.endAt ? (
                        <div className="truncate">{formatDateTime(notification.endAt)}</div>
                      ) : (
                        <Badge variant="warning">Permanent</Badge>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                      {notification.dismissalCount}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEditClick(notification)}
                          className="text-primary-700 hover:text-primary-900 transition-colors"
                          title="Edit"
                        >
                          <i className={`hm hm-${ICONS.EDIT}`} aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => setDeleteDialog({ open: true, notification })}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Delete"
                        >
                          <i className={`hm hm-${ICONS.DELETE}`} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data?.meta && data.meta.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {data.meta.page} of {data.meta.totalPages} ({data.meta.total} total)
            </div>
            <Pagination page={data.meta.page} totalPages={data.meta.totalPages} onPageChange={handlePageChange} />
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <NotificationFormModal
        open={showModal}
        onOpenChange={setShowModal}
        title={editingNotification ? 'Edit Notification' : 'Create Notification'}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        isLoading={createNotification.isPending || updateNotification.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, notification: null })}
        title="Delete Notification"
        description={`Are you sure you want to delete "${deleteDialog.notification?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        loading={deleteNotification.isPending}
      />
    </div>
  );
};

/**
 * Notification Form Modal Component
 * Separated for clarity and maintainability
 */
interface NotificationFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  formData: NotificationFormData;
  setFormData: (data: NotificationFormData) => void;
  onSubmit: (isActive: boolean) => Promise<void>;
  isLoading: boolean;
}

const NotificationFormModal = ({
  open,
  onOpenChange,
  title,
  formData,
  setFormData,
  onSubmit,
  isLoading,
}: NotificationFormModalProps) => {
  // Fetch data for multi-select dropdowns
  const { data: teamsData, isLoading: teamsLoading } = useAdminTeams({
    page: 1,
    limit: 1000,
  });
  const { data: usersData, isLoading: usersLoading } = useAdminUsers({
    page: 1,
    limit: 1000,
  });
  const { data: agenciesData, isLoading: agenciesLoading } = useAdminAgencies({
    page: 1,
    limit: 1000,
  });
  const { data: packagesData, isLoading: packagesLoading } = useAdminPackages({
    page: 1,
    limit: 100,
  });

  // Helper to check which quick duration button is active
  const getActiveDuration = (): '24h' | '7days' | '30days' | 'permanent' | null => {
    if (!formData.endAt) return 'permanent';

    const endDate = new Date(formData.endAt);
    const now = new Date();
    const diffMs = endDate.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffHours / 24;

    // Allow 1 hour tolerance for 24h
    if (Math.abs(diffHours - 24) < 1) return '24h';
    // Allow 12 hour tolerance for 7 days
    if (Math.abs(diffDays - 7) < 0.5) return '7days';
    // Allow 12 hour tolerance for 30 days
    if (Math.abs(diffDays - 30) < 0.5) return '30days';

    return null;
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="lg"
      footer={
        <div className="flex justify-between">
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => onSubmit(false)}
              loading={isLoading}
            >
              Save as Draft
            </Button>
            <Button onClick={() => onSubmit(true)} loading={isLoading}>
              Publish Now
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Details Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">
            Details
          </h4>

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Type *"
              value={formData.type}
              onValueChange={(value) =>
                setFormData({ ...formData, type: value })
              }
              options={TYPE_OPTIONS}
            />
            <Select
              label="Severity *"
              value={formData.severity}
              onValueChange={(value) =>
                setFormData({ ...formData, severity: value })
              }
              options={SEVERITY_OPTIONS}
            />
          </div>

          <div>
            <Input
              label="Title *"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="Enter notification title"
              maxLength={255}
            />
            <div className="text-xs text-gray-500 text-right mt-1">
              {formData.title.length}/255
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message *
            </label>
            <PostEditor
              value={formData.message}
              onChange={(text) => setFormData({ ...formData, message: text })}
              placeholder="Enter notification message..."
              maxLength={3000}
              autoFocus={false}
            />
          </div>
        </div>

        {/* Targeting Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">
            Targeting
          </h4>

          <Select
            label="Target Type *"
            value={formData.targetType}
            onValueChange={(value) =>
              setFormData({ ...formData, targetType: value, targetIds: [] })
            }
            options={TARGET_TYPE_OPTIONS}
          />

          {formData.targetType === 'TEAM' && (
            <MultiSelectDropdown
              label="Select Teams"
              value={formData.targetIds}
              onChange={(ids) => setFormData({ ...formData, targetIds: ids })}
              options={
                teamsData?.data?.map((t) => ({
                  id: t.id,
                  name: t.name,
                  subtitle: t.packageName || 'No package',
                })) || []
              }
              isLoading={teamsLoading}
              placeholder="Search and select teams..."
            />
          )}

          {formData.targetType === 'USER' && (
            <MultiSelectDropdown
              label="Select Users"
              value={formData.targetIds}
              onChange={(ids) => setFormData({ ...formData, targetIds: ids })}
              options={
                usersData?.data?.map((u) => ({
                  id: u.id,
                  name: u.fullName || u.email,
                  subtitle: u.email,
                })) || []
              }
              isLoading={usersLoading}
              placeholder="Search and select users..."
            />
          )}

          {formData.targetType === 'AGENCY' && (
            <MultiSelectDropdown
              label="Select Brand Portfolios"
              value={formData.targetIds}
              onChange={(ids) => setFormData({ ...formData, targetIds: ids })}
              options={
                agenciesData?.data?.map((a) => ({
                  id: a.id,
                  name: a.name,
                  subtitle: `${a.clientCount || 0} clients`,
                })) || []
              }
              isLoading={agenciesLoading}
              placeholder="Search and select brand portfolios..."
            />
          )}

          {formData.targetType === 'PACKAGE' && (
            <MultiSelectDropdown
              label="Select Packages"
              value={formData.targetIds}
              onChange={(ids) => setFormData({ ...formData, targetIds: ids })}
              options={
                packagesData?.data?.map((p) => ({
                  id: p.id,
                  name: p.name,
                  subtitle: p.slug,
                })) || []
              }
              isLoading={packagesLoading}
              placeholder="Search and select packages..."
            />
          )}
        </div>

        {/* Scheduling Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">
            Scheduling
          </h4>

          {/* Quick duration buttons */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Quick Duration
            </label>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  const end = new Date();
                  end.setHours(end.getHours() + 24);
                  setFormData({ ...formData, endAt: end.toISOString() });
                }}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md border transition-colors',
                  getActiveDuration() === '24h'
                    ? 'bg-primary-50 border-primary-300 text-primary-800 font-medium'
                    : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                )}
              >
                24h
              </button>
              <button
                type="button"
                onClick={() => {
                  const end = new Date();
                  end.setDate(end.getDate() + 7);
                  setFormData({ ...formData, endAt: end.toISOString() });
                }}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md border transition-colors',
                  getActiveDuration() === '7days'
                    ? 'bg-primary-50 border-primary-300 text-primary-800 font-medium'
                    : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                )}
              >
                7 days
              </button>
              <button
                type="button"
                onClick={() => {
                  const end = new Date();
                  end.setDate(end.getDate() + 30);
                  setFormData({ ...formData, endAt: end.toISOString() });
                }}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md border transition-colors',
                  getActiveDuration() === '30days'
                    ? 'bg-primary-50 border-primary-300 text-primary-800 font-medium'
                    : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                )}
              >
                30 days
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData({ ...formData, endAt: '' });
                }}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md border transition-colors',
                  getActiveDuration() === 'permanent'
                    ? 'bg-primary-50 border-primary-300 text-primary-800 font-medium'
                    : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                )}
              >
                Permanent
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <DatePickerSingle
              label="Start Date"
              value={formData.startAt}
              onChange={(iso) => setFormData({ ...formData, startAt: iso })}
              showTime
              placeholder="dd.mm.yyyy HH:mm"
            />
            <DatePickerSingle
              label="End Date"
              value={formData.endAt}
              onChange={(iso) => setFormData({ ...formData, endAt: iso })}
              showTime
              placeholder="dd.mm.yyyy HH:mm"
              minDate={formData.startAt ? new Date(formData.startAt) : undefined}
            />
          </div>

          {/* Warning for permanent notifications */}
          {!formData.endAt && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <i className={`hm hm-${ICONS.INFO} text-amber-600 mt-0.5`} aria-hidden="true" />
              <p className="text-sm text-amber-800">
                This notification will remain active until manually deactivated.
              </p>
            </div>
          )}

          <Checkbox
            label="Allow users to dismiss this notification"
            checked={formData.isDismissible}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, isDismissible: checked })
            }
          />
        </div>

        {/* Call to Action Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">
            Call to Action (Optional)
          </h4>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="CTA Button Text"
              value={formData.ctaText}
              onChange={(e) =>
                setFormData({ ...formData, ctaText: e.target.value })
              }
              placeholder="e.g., Learn More"
            />
            <Input
              label="CTA Button URL"
              value={formData.ctaUrl}
              onChange={(e) =>
                setFormData({ ...formData, ctaUrl: e.target.value })
              }
              placeholder="https://..."
            />
          </div>
        </div>

        {/* Preview Section */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
          <div
            className={cn(
              'p-4 rounded-lg border-l-4',
              formData.severity === 'INFO' && 'bg-blue-50 border-blue-500',
              formData.severity === 'WARNING' &&
                'bg-yellow-50 border-yellow-500',
              formData.severity === 'ERROR' && 'bg-red-50 border-red-500',
              formData.severity === 'SUCCESS' && 'bg-green-50 border-green-500'
            )}
          >
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'px-2 py-0.5 text-xs rounded',
                      TYPE_COLORS[formData.type]
                    )}
                  >
                    {formData.type}
                  </span>
                  <h5 className="font-semibold text-gray-900">
                    {formData.title || 'Notification Title'}
                  </h5>
                </div>
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                  {formData.message ||
                    'Your notification message will appear here...'}
                </p>
                {formData.ctaText && (
                  <button className="mt-2 px-3 py-1 bg-primary-600 text-primary-600-contrast text-sm rounded hover:bg-primary-700">
                    {formData.ctaText}
                  </button>
                )}
              </div>
              {formData.isDismissible && (
                <button className="text-gray-400 hover:text-gray-600">
                  <i className={`hm hm-${ICONS.CLOSE}`} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
